/**
 * @module lib/acs/acsAI
 * @description Motor IA del Asistente Conversacional Supervisado (ACS) con guardrails de citas.
 *
 * Wrapper sobre Gemini especializado en consultas normativas durante sesiones ACS.
 * A diferencia del asistente ISO genérico, este módulo impone un guardrail estricto:
 * **toda respuesta debe respaldarse con citas a documentos normativos reales cargados
 * en la base de datos** (`acs_norm_docs` + `acs_norm_chunks`).
 *
 * ## Flujo de validación de citas
 * 1. Gemini genera respuesta con lista de `citations` (docId + chunkId + fragmento)
 * 2. `validateCitations()` consulta PostgreSQL para verificar que los IDs existen
 * 3. Las citas con IDs inválidos se descartan
 * 4. Si no quedan citas válidas → se reemplaza la respuesta con `GUARDRAIL_MESSAGE`
 * 5. Las citas validadas se adjuntan al mensaje para que el CISO pueda revisarlas
 *
 * ## Guardrail
 * `GUARDRAIL_MESSAGE` es el mensaje estándar que se muestra cuando no hay respaldo
 * documental suficiente. No se permite al modelo "inventar" normativa.
 *
 * ## Funciones principales
 * | Función | Propósito |
 * |---------|-----------|
 * | `validateCitations()` | Verifica docId + chunkId contra la DB |
 * | `sendACSMessage()` | Envía mensaje al LLM y valida citas de la respuesta |
 *
 * @see ACSContext.tsx que llama a `sendACSMessage()` desde `sendSessionMessage()`
 * @see minutesSchema.ts para la validación de minutas generadas por el mismo LLM
 * @see types/acs.ts para los tipos ACSCitation y ACSCitationValidationResult
 */
// ============================================================
// ACS AI — Wrapper de sesión sobre el AI existente (Gemini)
//
// SEGURIDAD:
// - Todas las respuestas deben incluir citas con docId + chunkId reales.
// - Si no hay citas válidas → guardrail obligatorio.
// - Las citas se validan contra acs_norm_docs + acs_norm_chunks en DB.
// - No se permite inventar citas (si chunkId no existe en DB → guardrail).
//
// USO:
// - Llamado desde ACSContext.sendSessionMessage()
// - Recibe historial de sesión para mantener continuidad en la conversación
// ============================================================

import { getGeminiClient, DEFAULT_AI_CONFIG, incrementUsageCount } from '../ai/geminiClient';
import { supabase } from '../supabase';
import type { ACSCitation, ACSCitationValidationResult, ACSFrameworkCode, ACSSessionMessage } from '../../types/acs';

// ──────────────────────────────────────────────────────────
// GUARDRAIL — Mensaje estándar cuando no hay citas válidas
// ──────────────────────────────────────────────────────────

export const GUARDRAIL_MESSAGE =
  'No existe información suficiente en la normativa cargada para responder esta consulta con respaldo documental. ' +
  'Por favor consulte directamente la documentación oficial o contacte al CISO de su organización.';

// ──────────────────────────────────────────────────────────
// VALIDACIÓN DE CITAS
// ──────────────────────────────────────────────────────────

/**
 * Verifica que docId y chunkId existan en acs_norm_docs y acs_norm_chunks.
 * Si alguno es inválido, lo excluye del resultado final.
 * Si no quedan citas válidas, activa el guardrail.
 */
export async function validateCitations(
  citations: ACSCitation[],
  organizationId: string
): Promise<ACSCitationValidationResult> {
  if (!citations || citations.length === 0) {
    return {
      valid:              false,
      validCitations:     [],
      invalidIds:         [],
      guardrailTriggered: true,
    };
  }

  const docIds   = [...new Set(citations.map(c => c.docId))];
  const chunkIds = [...new Set(citations.map(c => c.chunkId))];

  const [docsResult, chunksResult] = await Promise.all([
    supabase
      .from('acs_norm_docs')
      .select('id')
      .in('id', docIds)
      .eq('organization_id', organizationId)
      .eq('is_active', true),
    supabase
      .from('acs_norm_chunks')
      .select('id')
      .in('id', chunkIds)
      .eq('organization_id', organizationId),
  ]);

  const validDocIds   = new Set((docsResult.data ?? []).map((d: { id: string }) => d.id));
  const validChunkIds = new Set((chunksResult.data ?? []).map((c: { id: string }) => c.id));

  const validCitations = citations.filter(
    c => validDocIds.has(c.docId) && validChunkIds.has(c.chunkId)
  );
  const invalidIds = citations
    .filter(c => !validDocIds.has(c.docId) || !validChunkIds.has(c.chunkId))
    .map(c => `doc:${c.docId} chunk:${c.chunkId}`);

  const guardrailTriggered = validCitations.length === 0;

  return {
    valid:              !guardrailTriggered,
    validCitations,
    invalidIds,
    guardrailTriggered,
  };
}

// ──────────────────────────────────────────────────────────
// EXTRACCIÓN DE CITAS DEL TEXTO DE GEMINI
// Se espera que Gemini devuelva citas en formato JSON al final de la respuesta:
// [CITAS]: [{"docId":"...","chunkId":"...","sectionRef":"...","quote":"..."}]
// ──────────────────────────────────────────────────────────

function extractCitationsFromResponse(text: string): { cleanText: string; rawCitations: ACSCitation[] } {
  const citationsMatch = text.match(/\[CITAS\]:\s*(\[[\s\S]*?\])/);

  if (!citationsMatch) {
    return { cleanText: text.trim(), rawCitations: [] };
  }

  const cleanText = text.replace(/\[CITAS\]:[\s\S]*$/, '').trim();

  try {
    const rawCitations = JSON.parse(citationsMatch[1]) as ACSCitation[];
    return { cleanText, rawCitations };
  } catch {
    return { cleanText, rawCitations: [] };
  }
}

// ──────────────────────────────────────────────────────────
// SYSTEM PROMPT ACS (inyectado en cada llamada de sesión)
// ──────────────────────────────────────────────────────────

function buildACSSystemPrompt(
  frameworkCode: ACSFrameworkCode,
  contextLabel: string,
  customInstruction?: string
): string {
  const orgContext = customInstruction?.trim()
    ? `\nCONTEXTO ORGANIZACIONAL:\n${customInstruction.trim()}\n`
    : '';

  return `Eres el Asistente Conversacional Supervisado (ACS) del sistema CGR de cumplimiento normativo.

SESIÓN ACTIVA:
- Framework: ${frameworkCode}
- Control/Requisito/Artículo: ${contextLabel}
${orgContext}

RESTRICCIONES OBLIGATORIAS:
1. Responde ÚNICAMENTE con base en la normativa cargada en el sistema.
2. Si no tienes información suficiente, responde exactamente: "${GUARDRAIL_MESSAGE}"
3. SIEMPRE incluye citas al final de tu respuesta en el formato exacto:
   [CITAS]: [{"docId":"UUID_REAL","chunkId":"UUID_REAL","sectionRef":"Cláusula X","quote":"Extracto relevante"}]
4. Si no puedes citar con docId y chunkId reales, NO inventes citas. Activa el guardrail.
5. No hagas afirmaciones fuera del alcance del framework indicado.
6. Mantén un tono técnico-profesional orientado a cumplimiento normativo.
7. Las propuestas de tareas son orientativas; la decisión final corresponde al CISO.

FORMATO DE CITAS:
Al final de cada respuesta, agrega una sección [CITAS] con array JSON.
Cada cita debe tener: docId (UUID de acs_norm_docs), chunkId (UUID de acs_norm_chunks).
Ejemplo:
[CITAS]: [{"docId":"550e8400-e29b-41d4-a716-446655440000","chunkId":"660e8400-e29b-41d4-a716-446655440001","sectionRef":"ISO 27001:2022 Cláusula 6.1.2","quote":"Las organizaciones deben establecer..."}]`;
}

// ──────────────────────────────────────────────────────────
// FUNCIÓN PRINCIPAL
// ──────────────────────────────────────────────────────────

export interface SendACSMessageParams {
  message:           string;
  meetingId:         string;
  frameworkCode:     ACSFrameworkCode;
  contextLabel:      string;
  sessionHistory:    ACSSessionMessage[];   // Historial de acs_session_messages
  organizationId:    string;
  customInstruction?: string;               // Instrucción extra del panel de configuración
}

export interface ACSMessageResult {
  content:        string;
  citations:      ACSCitation[];
  citationsValid: boolean;
  guardrailUsed:  boolean;
}

/**
 * Envía un mensaje en el contexto de una sesión ACS.
 * Extiende el Gemini client existente con:
 * - System prompt ACS (restricciones normativas)
 * - Historial de sesión específico del meeting
 * - Validación de citas contra DB
 * - Guardrail automático si no hay citas válidas
 */
export async function sendACSSessionMessage(
  params: SendACSMessageParams
): Promise<ACSMessageResult> {
  const client = getGeminiClient();
  if (!client) {
    throw new Error('El asistente de IA no está configurado. Por favor, configura tu API key en Settings.');
  }

  const { message, frameworkCode, contextLabel, sessionHistory, organizationId, customInstruction } = params;
  const systemPrompt = buildACSSystemPrompt(frameworkCode, contextLabel, customInstruction);

  // Convertir historial ACS al formato que Gemini espera
  const historyFormatted = sessionHistory
    .slice(-20)   // Mantener solo los últimos 20 mensajes para no exceder tokens
    .map(m => `${m.role === 'user' ? 'Contraparte' : 'Asistente ACS'}: ${m.content}`)
    .join('\n\n');

  const fullPrompt = `${systemPrompt}

HISTORIAL DE SESIÓN:
${historyFormatted || '(inicio de sesión)'}

CONTRAPARTE: ${message}

ASISTENTE ACS:`;

  const model = client.getGenerativeModel({
    model: DEFAULT_AI_CONFIG.model,
    generationConfig: {
      temperature: 0.3,   // Baja temperatura para respuestas más predecibles y normativas
      maxOutputTokens: DEFAULT_AI_CONFIG.maxTokens,
    },
  });

  let rawResponse: string;
  try {
    const result = await model.generateContent(fullPrompt);
    rawResponse = (await result.response).text();
    incrementUsageCount();
  } catch (error) {
    console.error('[ACS AI] ❌ Error Gemini:', error);
    const msg = error instanceof Error ? error.message : 'Error al comunicarse con el asistente ACS';
    throw new Error(msg);
  }

  // Extraer citas del texto de respuesta
  const { cleanText, rawCitations } = extractCitationsFromResponse(rawResponse);

  // Si no hay citas → guardrail inmediato (sin consultar DB)
  if (rawCitations.length === 0) {
    console.warn('[ACS AI] ⚠️ Respuesta sin citas — guardrail activado');
    return {
      content:        GUARDRAIL_MESSAGE,
      citations:      [],
      citationsValid: false,
      guardrailUsed:  true,
    };
  }

  // Validar citas contra DB
  const validation = await validateCitations(rawCitations, organizationId);

  if (validation.guardrailTriggered) {
    console.warn('[ACS AI] ⚠️ Citas inválidas en DB — guardrail activado', validation.invalidIds);
    return {
      content:        GUARDRAIL_MESSAGE,
      citations:      [],
      citationsValid: false,
      guardrailUsed:  true,
    };
  }

  if (validation.invalidIds.length > 0) {
    console.warn('[ACS AI] ⚠️ Algunas citas inválidas descartadas:', validation.invalidIds);
  }

  return {
    content:        cleanText,
    citations:      validation.validCitations,
    citationsValid: true,
    guardrailUsed:  false,
  };
}

// ──────────────────────────────────────────────────────────
// PROMPT DE GENERACIÓN DE MINUTAS
// Reutiliza buildMinutesPrompt de minutesSchema.ts
// ──────────────────────────────────────────────────────────

export async function generateMinutesRawText(
  prompt: string
): Promise<string> {
  const client = getGeminiClient();
  if (!client) {
    throw new Error('El asistente de IA no está configurado. Por favor, configura tu API key en Settings.');
  }

  const model = client.getGenerativeModel({
    model: DEFAULT_AI_CONFIG.model,
    generationConfig: {
      temperature: 0.2,
      maxOutputTokens: 2000,
    },
  });

  const result = await model.generateContent(prompt);
  const rawText = (await result.response).text();
  incrementUsageCount();
  return rawText;
}
